import React, { useEffect, useState } from "react";
import { Select, Table, message, Empty, Modal } from "antd";
import { Link } from "react-router-dom";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "../assets/css/paySlips.css";
import { companyID } from "../auth";
import { calculateSalaryBreakdown } from "../utils/calculateSalaryBreakdown";
import { formatDecimal } from "../utils/formatDecimal";
import { saveAs } from "file-saver";
import { convertDateFormate } from "../utils/convertDateFormate";
import api from "../api/axios";
import {
  getCompanySalaryConfig,
  getSalaryData,
} from "../services/salaryServices";

const { Option } = Select;

function PaySlips({ salaryConfig, employeeId }) {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [financialYear, setFinancialYear] = useState(currentYear);
  const [salaryData, setSalaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeeSalary, setEmployeeSalary] = useState(0);
  const companyId = companyID();
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfFileName, setPdfFileName] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const columns = [
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      render: (text) => {
        const monthYear = convertDateFormate(text);
        return new Date(monthYear.year, monthYear.month - 1).toLocaleString(
          "default",
          { month: "long" }
        );
      },
    },
    {
      title: "Total Salary",
      dataIndex: "total_salary",
      key: "total_salary",
      render: (text) => formatDecimal(text),
    },
    {
      title: "Reimbursement",
      dataIndex: "reimbursement_allowance",
      key: "reimbursement_allowance",
      render: (text) => formatDecimal(text),
    },
    {
      title: "Other deduction",
      dataIndex: "other_deduction",
      key: "other_deduction",
      render: (text) => formatDecimal(text),
    },
    {
      title: "Total Deduction",
      dataIndex: "total_deduction",
      key: "total_deduction",
      render: (text) => formatDecimal(text),
    },
    {
      title: "Net Salary",
      dataIndex: "net_salary",
      key: "net_salary",
      render: (text) => formatDecimal(text),
    },
    {
      title: "Action",
      dataIndex: "salary_slip",
      key: "payslip",
      render: (text, record) => {
        return (
          <Link
            to="#"
            className="btn btn-sm btn-primary"
            onClick={(e) => {
              e.preventDefault();
              setLoading(true);
              api
                .get(`${text}`, {
                  responseType: "blob",
                })
                .then((res) => {
                  const pdfBlob = new Blob([res.data], {
                    type: "application/pdf",
                  });
                  const pdfUrl = URL.createObjectURL(pdfBlob);
                  setPdfUrl(pdfUrl);
                  setPdfFileName(text);
                  setIsModalVisible(true);
                })
                .catch((err) => {
                  console.error(err);
                })
                .finally(() => {
                  setLoading(false);
                });
            }}
          >
            View Slip
          </Link>
        );
      },
    },
  ];

  useEffect(() => {
    setLoading(true);
    api
      .get(`salary/${employeeId}/${financialYear}`)
      .then((response) => {
        setSalaryData(response.data.data);
      })
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          setSalaryData([]);
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [financialYear]);

  const fetchCompanySalaryConfig = async (salary) => {
    try {
      const { config } = await getCompanySalaryConfig(companyId);
      const filteredData = await getSalaryData(salary, config);

      setSalaryRecords(
        filteredData.filter((salary) => salary.salary_status === 1)
      );
    } catch (error) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    if (salaryData.length) {
      fetchCompanySalaryConfig(salaryData);
    } else {
      setSalaryRecords([]);
    }
  }, [salaryData, companyId]);

  const handleFinancialYearChange = (value) => {
    setFinancialYear(value);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
    setPdfFileName("");
  };

  const handleDownload = () => {
    if (pdfUrl && pdfFileName) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = pdfFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const startYear = 2000;

  const financialYears = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => {
      const year = startYear + i;
      return `${year}`;
    }
  );

  return (
    <div className="pay-slips">
      <h2>Pay slips for {financialYear}</h2>
      <div className="financialYearDropdown mb-2">
        <label>Select Financial Year:</label>
        <Select
          value={financialYear}
          style={{ width: 120 }}
          onChange={handleFinancialYearChange}
        >
          {financialYears.map((year) => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>
      </div>
      <div className="row">
        <div className="table-responsive">
          {salaryRecords.length > 0 ? (
            <Table
              dataSource={salaryRecords}
              columns={columns}
              rowKey="month"
              loading={loading}
              pagination={false}
            />
          ) : !loading ? (
            <Empty description="No salary details available." />
          ) : (
            <Empty description="Fetching salary details..." />
          )}
        </div>
      </div>

      <Modal
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Pay Slip - {pdfFileName}</span>
            <div className="pr-16" >
              <button
                className="btn btn-outline-primary btn-sm me-2"
                onClick={handleDownload}
                title="Download PDF"
              >
                <i className="fas fa-download"></i> Download
              </button>
            </div>
          </div>
        }
        open={isModalVisible}
        onCancel={handleModalClose}
        width="75vw"
        bodyStyle={{ padding: 0, height: "80vh" }}
        footer={null}
        centered={true}
        closable={true}
        closeIcon={
          <span style={{ 
            fontSize: '18px', 
            color: '#666',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}>
            ×
          </span>
        }
        maskClosable={true}
        destroyOnClose={true}
        wrapClassName="payslip-pdf-modal"
      >
        {pdfUrl ? (
          <div style={{ width: "100%", height: "80vh" }}>
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <Viewer
                fileUrl={pdfUrl}
                defaultScale={1.0}
              />
            </Worker>
          </div>
        ) : (
          <div
            style={{
              height: "80vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "16px",
              color: "#666",
            }}
          >
            Loading PDF...
          </div>
        )}
      </Modal>
    </div>
  );
}

export default PaySlips;
